import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from '@firebase/firestore';
import { db, getFunctions, httpsCallable } from '../../firebase';
import { useAuth } from '../../components/context/AuthContext';
import Layout from '../../components/ui/Layout'; 
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import FCMStatusChecker from '../../components/ui/FCMStatusChecker';
import { Container, Row, Col, Button, Alert, Table, Spinner, Form } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';

const logger = createLogger('AdminDashboard');

function AdminDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setErrorPage] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const availableRoles = ['Regular', 'Admin', 'Employee'];

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/admin", label: "Admin Panel" },
    { label: "User Account Management" }
  ];

  const clearMessages = () => { setErrorPage(null); setSuccessMessage(''); };

  // Check if Cloud Functions are accessible
  const checkFunctionsAvailability = async () => {
    try {
      // Just test the connection by creating a callable, don't actually call it
      httpsCallable(getFunctions(), 'setUserRole');
      httpsCallable(getFunctions(), 'fixUsersWithoutAuthId');
      return true;
    } catch (err) {
      return false;
    }
  };

  const forceTokenRefresh = () => {
    if (currentUser) {
      setSuccessMessage(prev => `${prev  } Forcing token refresh...`);
      setTimeout(() => {
        currentUser.getIdToken(true)
          .then(() => setSuccessMessage(prev => prev.replace(' Forcing token refresh...', ' Token refreshed.')))
          .catch(err => setErrorPage(prev => prev ? `${prev} | Token refresh failed.` : 'Token refresh failed.'));
      }, 1500);
    }
  };

  // Bootstrap first admin (setupRosueProAdmin) - no admin required, only for rosuepro@gmail.com
  const bootstrapFirstAdmin = async () => {
    if (!currentUser || currentUser.email !== 'rosuepro@gmail.com') {
      setErrorPage('Bootstrap is only for rosuepro@gmail.com. Use Firebase Console for other users.');
      return;
    }
    clearMessages(); setLoading(true);
    try {
      const setupCallable = httpsCallable(getFunctions(), 'setupRosueProAdmin');
      const result = await setupCallable({});
      setSuccessMessage(result.data.message);
      await currentUser.getIdToken(true);
      setSuccessMessage(prev => `${prev} Please refresh the page.`);
    } catch (err) {
      logger.error('Bootstrap admin error:', err);
      setErrorPage(`Bootstrap failed: ${err.message || 'Unknown error'}`);
    }
    setLoading(false);
  };

  // Function to manually set admin claims for current user (requires existing admin)
  const setCurrentUserAsAdmin = async () => {
    if (!currentUser) {
      setErrorPage('No user logged in');
      return;
    }
    
    clearMessages(); setLoading(true);
    try {
      logger.debug('Setting current user as admin...');
      const setUserRoleCallable = httpsCallable(getFunctions(), 'setUserRole');
      const result = await setUserRoleCallable({ 
        userId: currentUser.uid, 
        role: 'Admin', 
        employeeSupermarketId: null 
      });
      setSuccessMessage(`Admin privileges granted: ${result.data.message}`);
      
      // Force token refresh to get new claims
      await currentUser.getIdToken(true);
      setSuccessMessage(prev => `${prev  } Token refreshed with new admin claims.`);
      
    } catch (err) {
      logger.error('Set admin error:', err);
      setErrorPage(`Failed to set admin privileges: ${err.message || 'Unknown error'}`);
    }
    setLoading(false);
  };

  // Function to check current user's authentication status
  const checkCurrentUserStatus = async () => {
    if (!currentUser) {
      setErrorPage('No user logged in');
      return;
    }
    
    try {
      const token = await currentUser.getIdTokenResult();
      logger.debug('Current user token claims:', token.claims);
      
      const statusInfo = `
        User: ${currentUser.email}
        UID: ${currentUser.uid}
        Admin: ${token.claims.isAdmin ? 'Yes' : 'No'}
        Employee: ${token.claims.isSupermarketEmployee ? 'Yes' : 'No'}
        Supermarket ID: ${token.claims.supermarketId || 'None'}
      `;
      
      setSuccessMessage(`Authentication Status:\n${statusInfo}`);
    } catch (err) {
      logger.error('Check status error:', err);
      setErrorPage(`Failed to check status: ${err.message}`);
    }
  };

  // NOTE: Admin setup should be done through Firebase Console or Firebase Admin SDK
  // Hardcoded admin setup functions have been removed for security reasons
  // To set up admin users:
  // 1. Use Firebase Console to set custom claims
  // 2. Use Firebase Admin SDK in a secure server environment
  // 3. Update user role in Firestore Users collection

  const fetchSupermarkets = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Supermarkets'));
      setSupermarkets(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setErrorPage(prev => prev ? `${prev} | Failed to fetch hardware stores.` : 'Failed to fetch hardware stores.');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Users'));
      const usersData = querySnapshot.docs.map(doc => ({ firestoreDocId: doc.id, ...doc.data() }));
      
      // Check for users without identity_id and log them
      const usersWithoutAuthId = usersData.filter(user => !user.identity_id || user.identity_id === 'MISSING_AUTH_UID');
      if (usersWithoutAuthId.length > 0) {
        logger.warn('Found users without proper Auth ID:', usersWithoutAuthId);
        setErrorPage(`Warning: ${usersWithoutAuthId.length} user(s) found without proper Auth ID. These users cannot be deleted from Auth.`);
      }
      
      setUsers(usersData);
    } catch (err) {
      setErrorPage('Failed to fetch users.');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      clearMessages(); setLoading(true);
      try {
        // Check if Cloud Functions are accessible
        const functionsAvailable = await checkFunctionsAvailability();
        if (!functionsAvailable) {
          logger.warn('Cloud Functions may not be properly deployed or accessible');
        }
        
        await Promise.all([fetchUsers(), fetchSupermarkets()]);
      } catch (error) {
        logger.error('Error loading data:', error);
        setErrorPage('Failed to load data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchUsers, fetchSupermarkets]);

  const handleRoleOrAssignmentChange = async (targetAuthUid, newRole, newEmployeeSupermarketId, email) => {
    clearMessages(); setLoading(true);
    if (!targetAuthUid) {
      setErrorPage("Cannot update user: User Auth ID missing."); setLoading(false); return;
    }
    
    try {
      // First, refresh the current user's token to ensure custom claims are up to date
      if (currentUser) {
        logger.debug('Refreshing user token to ensure custom claims are current...');
        await currentUser.getIdToken(true);
        logger.debug('Token refreshed successfully');
      }
      
      const setUserRoleCallable = httpsCallable(getFunctions(), 'setUserRole');
      const result = await setUserRoleCallable({ 
        userId: targetAuthUid, 
        role: newRole, 
        employeeSupermarketId: (newRole === 'Employee' && newEmployeeSupermarketId) ? newEmployeeSupermarketId : null,
        email: email || null
      });
      setSuccessMessage(result.data.message);
      const resolvedUserId = result.data.resolvedUserId || targetAuthUid;
      setUsers(prevUsers => prevUsers.map(user =>
        (user.identity_id === targetAuthUid || user.identity_id === resolvedUserId)
          ? { ...user, role_name: newRole, employeeSupermarketId: (newRole === 'Employee' && newEmployeeSupermarketId) ? newEmployeeSupermarketId : null, identity_id: resolvedUserId }
          : user
      ));
      if (currentUser && (currentUser.uid === targetAuthUid || currentUser.uid === resolvedUserId)) forceTokenRefresh();
    } catch (err) {
      logger.error('Role update error:', err);

      // Provide more specific error messages
      let errorMessage = 'Unknown error occurred';
      if (err.code === 'functions/failed-precondition') {
        errorMessage = 'Permission denied: You must be an admin user to perform this action. Please ensure your account has admin privileges.';
      } else if (err.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication required: Please log in again.';
      } else if (err.code === 'functions/internal') {
        errorMessage = 'Internal server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setErrorPage(`Role update failed: ${errorMessage}`);
    }
    setLoading(false);
  };

  const handleDeleteUser = async (firestoreDocId, identity_id, userName) => {
    const confirmMessage = identity_id 
      ? `Are you sure you want to delete "${userName}" from Auth and Firestore? This cannot be undone.`
      : `Are you sure you want to delete "${userName}" from Firestore only? (No Auth record exists)`;
    
    if (!window.confirm(confirmMessage)) return;
    setErrorPage(null); setSuccessMessage(''); setLoading(true);
    
    try {
      let result;
      
      if (identity_id) {
        // Try to delete from both Auth and Firestore
        const deleteUserCallable = httpsCallable(getFunctions(), 'deleteUserAccount');
        
        // Add timeout to the call
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout - function may not be deployed')), 30000)
        );
        
        const resultPromise = deleteUserCallable({ userId: identity_id });
        result = await Promise.race([resultPromise, timeoutPromise]);
        
        setUsers(prev => prev.filter(u => u.identity_id !== identity_id));
      } else {
        // Delete only from Firestore (for users without Auth records)
        const deleteFirestoreCallable = httpsCallable(getFunctions(), 'deleteFirestoreUser');
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout - function may not be deployed')), 30000)
        );
        
        const resultPromise = deleteFirestoreCallable({ firestoreDocId });
        result = await Promise.race([resultPromise, timeoutPromise]);
        
        setUsers(prev => prev.filter(u => u.id !== firestoreDocId));
      }
      
      setSuccessMessage(result.data.message);
    } catch (err) {
      logger.error('Delete user error:', err);
      logger.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
      
      let errorMessage = 'Unknown error occurred';
      if (err.code === 'functions/unavailable') {
        errorMessage = 'Cloud Function is not available. Please check if functions are deployed.';
      } else if (err.code === 'functions/internal') {
        errorMessage = 'Internal server error. Please try again later.';
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = 'Permission denied. Only admins can delete users.';
      } else if (err.code === 'functions/not-found') {
        errorMessage = 'User document not found.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Request timed out. The Cloud Function may not be deployed or is not responding.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setErrorPage(`Delete failed: ${errorMessage}`);
    }
    setLoading(false);
  };

  const handleFixUsersWithoutAuthId = async () => {
    if (!window.confirm("This will attempt to fix users without proper Auth IDs. Users with matching emails will be updated, orphaned documents will be deleted. Continue?")) return;
    setErrorPage(null); setSuccessMessage(''); setLoading(true);
    try {
      const fixUsersCallable = httpsCallable(getFunctions(), 'fixUsersWithoutAuthId');
      
      // Add timeout to the call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout - function may not be deployed')), 30000)
      );
      
      const resultPromise = fixUsersCallable({});
      const result = await Promise.race([resultPromise, timeoutPromise]);
      
      setSuccessMessage(result.data.message);
      
      // Reload users to reflect changes
      await fetchUsers();
      
      // Show detailed results if available
      if (result.data.details && result.data.details.length > 0) {
        // Show a more detailed success message with the results
        if (result.data.details.length > 5) {
          setSuccessMessage(prev => `${prev} (${result.data.details.length - 5} more actions - see console for details)`);
        }
      }
    } catch (err) {
      logger.error('Fix users error:', err);
      logger.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
      
      let errorMessage = 'Unknown error occurred';
      if (err.code === 'functions/unavailable') {
        errorMessage = 'Cloud Function is not available. Please check if functions are deployed.';
      } else if (err.code === 'functions/internal') {
        errorMessage = 'Internal server error. Please try again later.';
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = 'Permission denied. Only admins can fix user data.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Request timed out. The Cloud Function may not be deployed or is not responding.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'CORS error - function may not be deployed. Please try again in a few minutes.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setErrorPage(`Fix failed: ${errorMessage}`);
    }
    setLoading(false);
  };

  // Minimal inline styles needed now, rely on Bootstrap and generic-styles.css
  const thStyles = { backgroundColor: 'var(--primary-deep-green)', color: 'var(--text-white)' };

  if (loading && users.length === 0) {
      return <Layout><Container className="text-center mt-5"><Spinner animation="border" /> <p>Loading User Dashboard...</p></Container></Layout>;
  }

  return (
    <Layout>
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        
        <h1 className="mb-3 mt-3 text-center">User Account Management</h1>
        
        {pageError && <Alert variant="danger">{pageError}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {/* FCM Status Checker */}
        <Row className="mb-4">
          <Col>
            <FCMStatusChecker />
          </Col>
        </Row>

        {/* Fix Users Without Auth ID Button */}
        <div className="mb-3 text-center">
          <Button 
            variant="warning" 
            onClick={handleFixUsersWithoutAuthId}
            disabled={loading}
            className="me-2"
          >
            <i className="fas fa-wrench me-2"></i>
            Fix Users Without Auth ID
          </Button>
          <small className="text-muted d-block mt-1">
            This will fix users missing identity_id or clean up orphaned documents
          </small>
        </div>

                 {/* Admin Authentication Buttons */}
         <div className="mb-3 text-center">
           {currentUser?.email === 'rosuepro@gmail.com' && (
             <Button 
               variant="success" 
               onClick={bootstrapFirstAdmin}
               disabled={loading}
               className="me-2"
             >
               <i className="fas fa-key me-2"></i>
               Bootstrap First Admin (rosuepro)
             </Button>
           )}
           <Button 
             variant="info" 
             onClick={setCurrentUserAsAdmin}
             disabled={loading || !currentUser}
             className="me-2"
           >
             <i className="fas fa-crown me-2"></i>
             Set Current User as Admin
           </Button>
           <Button 
             variant="secondary" 
             onClick={checkCurrentUserStatus}
             disabled={loading || !currentUser}
             className="me-2"
           >
             <i className="fas fa-info-circle me-2"></i>
             Check Current User Status
           </Button>
          <small className="text-muted d-block mt-2">
            Admin privileges should be managed through Firebase Console or Firebase Admin SDK.
          </small>
         </div>

        <section className="mt-4">
          {loading && users.length > 0 && <div className="text-center my-2"><Spinner animation="border" size="sm" /><span> Updating...</span></div>}
          {users.length === 0 && !loading ? (
            <Alert variant="info">No users found.</Alert>
          ) : (
            // Using Bootstrap classes for table styling primarily
            <Table bordered hover responsive striped className="shadow-sm rounded-3 overflow-hidden">
              <thead style={thStyles}>
                <tr>
                  <th>Name</th>
                  <th>Auth UID (identity_id)</th>
                  <th>Current Role</th>
                  <th>Assigned Supermarket</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.firestoreDocId}>
                    <td>{user.first_name || 'N/A'}</td>
                    <td>{user.identity_id || 'MISSING_AUTH_UID'}</td>
                    <td>
                      <Form.Select 
                        size="sm"
                        value={user.role_name}
                        onChange={(e) => handleRoleOrAssignmentChange(user.identity_id, e.target.value, user.employeeSupermarketId, user.email)}
                        disabled={loading || !user.identity_id}
                      >
                        {availableRoles.map(role => (<option key={role} value={role}>{role}</option>))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select 
                        size="sm"
                        value={user.employeeSupermarketId || ''} 
                        onChange={(e) => handleRoleOrAssignmentChange(user.identity_id, user.role_name, e.target.value || null, user.email)}
                        disabled={loading || user.role_name !== 'Employee' || !user.identity_id}
                        title={user.role_name !== 'Employee' ? 'Change role to Employee to assign' : 'Assign supermarket'}
                      >
                        <option value="">Not Assigned</option>
                        {supermarkets.map(s => (<option key={s.id} value={s.id}>{s.display_name || s.name || s.id}</option>))}
                      </Form.Select>
                    </td>
                    <td>
                      <Button 
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.firestoreDocId, user.identity_id, user.first_name || 'Unknown User')} 
                        disabled={loading}
                      >
                        Delete User (FS Only)
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      </Container>
    </Layout>
  );
}

export default AdminDashboard;
